import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, Loader2, User, Building2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function RegisterPage() {
  const { register, error } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    business_name: "",
    agree_terms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.first_name || !formData.last_name) {
      setFormError("Please enter your full name.");
      return false;
    }
    if (!formData.email) {
      setFormError("Please enter your email address.");
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      setFormError("Password must be at least 8 characters long.");
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setFormError("Passwords do not match.");
      return false;
    }
    if (!formData.agree_terms) {
      setFormError("Please agree to the terms and conditions.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!validateForm()) return;

    setLoading(true);
    const result = await register({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      password: formData.password,
      business_name: formData.business_name,
    });
    setLoading(false);

    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setFormError(result.error);
    }
  };

  const passwordStrength = () => {
    const password = formData.password;
    if (!password) return { score: 0, label: "", color: "" };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    
    const levels = [
      { label: "Weak", color: "bg-[#EF4444]" },
      { label: "Fair", color: "bg-[#F59E0B]" },
      { label: "Good", color: "bg-[#10B981]" },
      { label: "Strong", color: "bg-[#7C3AED]" },
    ];
    
    return { score, ...levels[Math.min(score, 3)] };
  };

  const strength = passwordStrength();

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/h2gv38bu_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%281%29.png"
              alt="LocalBest.ai"
              className="h-14 w-auto object-contain"
            />
          </Link>
        </div>

        <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
              Create Your Account
            </h2>
            <p className="text-[#94A3B8]">
              Start managing your directory presence today
            </p>
          </div>

          {/* Error Message */}
          {(formError || error) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg"
            >
              <p className="text-[#EF4444] text-sm">{formError || error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="John"
                    className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="register-first-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  Last Name
                </label>
                <Input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Doe"
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="register-last-name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="register-email"
                />
              </div>
            </div>

            {/* Business Name (Optional) */}
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Business Name <span className="text-[#64748B]">(Optional)</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  placeholder="Your Business Name"
                  className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="register-business"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  className="pl-11 pr-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password Strength */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < strength.score ? strength.color : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-[#64748B]">Password strength: {strength.label}</p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="register-confirm-password"
                />
                {formData.confirm_password && formData.password === formData.confirm_password && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]" />
                )}
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="agree_terms"
                checked={formData.agree_terms}
                onChange={handleChange}
                className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#7C3AED] focus:ring-[#7C3AED]"
                data-testid="register-terms"
              />
              <label className="text-sm text-[#94A3B8]">
                I agree to the{" "}
                <Link to="/terms" className="text-[#7C3AED] hover:text-[#8B5CF6]">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#7C3AED] hover:text-[#8B5CF6]">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#8B5CF6] hover:to-[#F472B6] text-white font-semibold text-base"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#94A3B8]">
              Already have an account?{" "}
              <Link
                to="/auth/login"
                className="text-[#7C3AED] hover:text-[#8B5CF6] font-medium transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
